import { Box, Typography, CircularProgress } from '@mui/material';
import { useEffect, useRef } from 'react';

interface StreamingMessageProps {
  content: string;
  isStreaming?: boolean;
}

/**
 * Component for displaying streaming messages with typing indicator.
 */
export const StreamingMessage = ({ content, isStreaming = false }: StreamingMessageProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll to bottom as content streams
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [content]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body1" component="div" ref={contentRef}>
        {content}
        {isStreaming && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 8,
              height: 16,
              bgcolor: 'text.primary',
              ml: 0.5,
              animation: 'blink 1s infinite',
              '@keyframes blink': {
                '0%, 50%': { opacity: 1 },
                '51%, 100%': { opacity: 0 },
              },
            }}
          />
        )}
      </Typography>
      {isStreaming && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Agent is thinking...
          </Typography>
        </Box>
      )}
    </Box>
  );
};
